import { NOTE_DECISION_TYPE, paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useRef } from 'react';

// ---------- CREATE TASK ----------

const createTaskRequest = async ({
  recurrence,
  program,
  ...props
}: paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']) => {
  const response = axiosInstance.post<
    paths['/task/create-custom-task']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/task/create-custom-task']['post']['responses']['201']['content']['application/json']
    >
  >(`/task/create-custom-task`, {
    ...(recurrence && { recurrence: Number(recurrence) }),
    ...(program && { program }),
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateTaskMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createTask'],
    mutationFn: (
      props: paths['/task/create-custom-task']['post']['requestBody']['content']['application/json'],
    ) => createTaskRequest(props),
    onSuccess: (res, req) =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['tasks'] }),
        client.invalidateQueries({
          queryKey: ['patient', String(req.patient_id)],
        }),
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
      ]),
  });

  return mutation;
};

// ---------- SAVE TASK ----------

const saveTaskRequest = async ({
  task_id,
  recurrence,
  program,
  signal,
  ...props
}: paths['/task/{task_id}/save']['post']['requestBody']['content']['application/json'] & {
  task_id: string;
  signal: AbortSignal;
}) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/save']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/task/{task_id}/save']['post']['responses']['200']['content']['application/json']
    >
  >(
    `/task/${task_id}/save`,
    {
      ...(recurrence && { recurrence: Number(recurrence) }),
      program: program ? program : null,
      ...props,
    },
    { signal },
  );

  return response;
};

export const useSaveTaskMutation = (task_id: string) => {
  const client = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationKey: ['saveTask', task_id],
    mutationFn: (
      props: paths['/task/{task_id}/save']['post']['requestBody']['content']['application/json'],
    ) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      return saveTaskRequest({
        ...props,
        task_id,
        signal: abortControllerRef.current.signal,
      });
    },
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
        client.invalidateQueries({
          queryKey: ['assessment'],
        }),
      ]),
  });

  return mutation;
};

// ---------- ASSIGN TASK ----------

const assignTaskRequest = async ({
  task_id,
  ...props
}: paths['/task/{task_id}/assign']['post']['requestBody']['content']['application/json'] & {
  task_id: string;
}) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/assign']['post']['requestBody']['content']['application/json']
  >(`/task/${task_id}/assign`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useAssignTaskMutation = (task_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['assignTask'],
    mutationFn: (
      props: paths['/task/{task_id}/assign']['post']['requestBody']['content']['application/json'],
    ) => assignTaskRequest({ ...props, task_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['tasks'] }),
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
      ]),
  });

  return mutation;
};

// ---------- COMPLETE TASK ----------

interface OnTaskCompletingBody {
  final?: boolean;
  note?: string;
  note_decision?: NOTE_DECISION_TYPE;
}

const completeTaskRequest = async ({
  task_id,
  final,
  note_decision,
  note,
}: {
  task_id: string;
} & OnTaskCompletingBody) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/complete']['post']['requestBody'],
    AxiosResponse<
      paths['/task/{task_id}/complete']['post']['responses']['200']['content']['application/json']
    >
  >(`/task/${task_id}/complete`, {
    final,
    note_decision,
    note,
  });
  const data = await response;

  return data;
};

export const useCompleteTaskMutation = (taskId: string, patientId?: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['completeTask'],
    mutationFn: (props: OnTaskCompletingBody) =>
      taskId
        ? completeTaskRequest({
            task_id: taskId,
            ...props,
          })
        : Promise.reject(new Error('Task could not load')),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['tasks'] }),
        client.invalidateQueries({
          queryKey: ['task', taskId],
        }),
        client.invalidateQueries({
          queryKey: ['patient', patientId],
        }),
        client.invalidateQueries({
          queryKey: ['programStatusHistory', patientId],
        }),
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
        client.invalidateQueries({
          queryKey: ['careManagementActivities', patientId],
        }),
      ]),
  });

  return mutation;
};

// ---------- LOCK / UNLOCK / UNLOCK REQUESTS ----------

const taskLockRequest = async (task_id: string) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/lock']['post']['responses']['200']['content']
  >(`/task/${task_id}/lock`);
  const data = await response;

  return data;
};

export const useTaskLockMutation = (task_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['taskLock'],
    mutationFn: () => taskLockRequest(task_id),
    onError: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
      ]),
  });

  return mutation;
};

const taskUnlockRequest = async (task_id: string) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/unlock']['post']['responses']['200']['content']
  >(`/task/${task_id}/unlock`);
  const data = await response;

  return data;
};

export const useTaskUnlockMutation = (task_id: string) => {
  const mutation = useMutation({
    mutationKey: ['taskUnlock'],
    mutationFn: () => taskUnlockRequest(task_id),
  });

  return mutation;
};

const unlockLockedTaskRequest = async (task_id: string) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/unlock-request']['post']['responses']['200']['content']
  >(`/task/${task_id}/unlock-request`);
  const data = await response;

  return data;
};

export const useLockedTaskUnlockMutation = (task_id: string) => {
  const mutation = useMutation({
    mutationKey: ['unlockLockedTask'],
    mutationFn: () => unlockLockedTaskRequest(task_id),
  });

  return mutation;
};

const replyToTaskUnlockRequest = async (task_id: string, approved: boolean) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/unlock-request/reply']['post']['responses']['200']['content']
  >(`/task/${task_id}/unlock-request/reply`, { approved });
  const data = await response;

  return data;
};

export const useReplyUnlockTaskRequestMutation = (task_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['replyUnlockLockedTaskRequest'],
    mutationFn: ({ approve }: { approve: boolean }) =>
      replyToTaskUnlockRequest(task_id, approve),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
      ]),
  });

  return mutation;
};

// ---------- OUTREACH ATTEMPT ----------

const createOutreachAttemptRequest = async ({
  task_id,
  ...props
}: paths['/task/{task_id}/outreach']['post']['requestBody']['content']['application/json'] & {
  task_id: string;
}) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/outreach']['post']['requestBody']['content']['application/json']
  >(`/task/${task_id}/outreach`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateOutreachAttemptMutation = (task_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createOutreachAttempt'],
    mutationFn: (
      props: paths['/task/{task_id}/outreach']['post']['requestBody']['content']['application/json'],
    ) => createOutreachAttemptRequest({ ...props, task_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
        client.invalidateQueries({
          queryKey: ['assessment'],
        }),
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
      ]),
  });

  return mutation;
};

// ---------- TASK COMMENT ----------

const createTaskCommentRequest = async ({
  task_id,
  ...props
}: paths['/task/{task_id}/comment']['post']['requestBody']['content']['application/json'] & {
  task_id: string;
}) => {
  const response = axiosInstance.post<
    paths['/task/{task_id}/comment']['post']['requestBody']['content']['application/json']
  >(`/task/${task_id}/comment`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateTaskCommentMutation = (task_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['taskComment'],
    mutationFn: (
      props: paths['/task/{task_id}/comment']['post']['requestBody']['content']['application/json'],
    ) => createTaskCommentRequest({ ...props, task_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
      ]),
  });

  return mutation;
};

const editTaskCommentRequest = async ({
  comment_id,
  ...props
}: paths['/task/comment/{comment_id}']['patch']['requestBody']['content']['application/json'] & {
  comment_id: string;
}) => {
  const response = axiosInstance.patch<
    paths['/task/comment/{comment_id}']['patch']['requestBody']['content']['application/json']
  >(`/task/comment/${comment_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditTaskCommentMutation = ({
  comment_id,
  task_id,
}: {
  comment_id: string;
  task_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editTaskComment'],
    mutationFn: (
      props: paths['/task/comment/{comment_id}']['patch']['requestBody']['content']['application/json'],
    ) => editTaskCommentRequest({ ...props, comment_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
      ]),
  });

  return mutation;
};

const deleteTaskCommentRequest = async ({
  comment_id,
}: {
  comment_id: string;
}) => {
  const response = axiosInstance.delete<
    paths['/task/comment/{comment_id}']['delete']['requestBody']
  >(`/task/comment/${comment_id}`);
  const data = await response;

  return data;
};

export const useDeleteTaskCommentMutation = ({
  comment_id,
  task_id,
}: {
  comment_id: string;
  task_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editTaskComment'],
    mutationFn: () => deleteTaskCommentRequest({ comment_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['task', task_id],
        }),
      ]),
  });

  return mutation;
};

const updateWorklistTablePreferencesRequest = async (
  props: Partial<
    paths['/preferences/task-table']['patch']['requestBody']['content']['application/json']
  >,
) => {
  const response = axiosInstance.patch<
    paths['/preferences/task-table']['patch']['requestBody']['content']['application/json']
  >(`/preferences/task-table`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useUpdateWorklistTablePreferencesMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['task', 'table', 'preferences', 'edit'],
    mutationFn: (
      props: Partial<
        paths['/preferences/task-table']['patch']['requestBody']['content']['application/json']
      >,
    ) => updateWorklistTablePreferencesRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['tasks'] }),
        client.invalidateQueries({
          queryKey: ['task', 'table', 'preferences'],
        }),
      ]),
  });

  return mutation;
};

const deleteWorklistTablePreferencesRequest = async () => {
  const response = axiosInstance.delete(`/preferences/task-table`);
  const data = await response;

  return data;
};

export const useDeleteWorklistTablePreferencesMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['task', 'table', 'preferences', 'delete'],
    mutationFn: () => deleteWorklistTablePreferencesRequest(),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['tasks'] }),
        client.invalidateQueries({
          queryKey: ['task', 'table', 'preferences'],
        }),
      ]),
  });

  return mutation;
};
